import type { ScheduledRunRecord } from './use-scheduled-run-outcomes';

/**
 * Matching scheduled occurrences against the runs that actually happened.
 *
 * Extracted from the schedule view so the rules can be tested directly — the
 * tolerance window and the grace period are the two judgement calls that decide
 * whether a slot reads as "ran" or as "no run recorded", and getting them wrong
 * either invents failures or hides them.
 */

/** Widest gap either side of a scheduled time that a run may be matched to. */
export const MAX_MATCH_TOLERANCE_MS = 30 * 60 * 1000;

/**
 * How far either side of the requested range run history must be fetched.
 *
 * A run may be matched to a slot up to a full tolerance away, so a window cut to
 * the exact range cannot answer for the slots at its edges: a Sunday 23:50 slot
 * whose run started Monday 00:05 had no row to match against and read as "No run
 * recorded". Padding by the widest tolerance any trigger can earn makes the
 * fetched history a superset of what the matcher can reach for.
 */
export function paddedHistoryWindow(range: { fromMs: number; toMs: number }): {
  fromMs: number;
  toMs: number;
} {
  return {
    fromMs: range.fromMs - MAX_MATCH_TOLERANCE_MS,
    toMs: range.toMs + MAX_MATCH_TOLERANCE_MS,
  };
}

/** What happened at one scheduled occurrence. */
export type OccurrenceOutcome =
  | { kind: 'ran'; status: string }
  /** Past, and no run landed near it — the schedule didn't produce a run. */
  | { kind: 'no-record' }
  /** History isn't in yet, or the slot is still inside its grace window. */
  | { kind: 'pending' }
  | { kind: 'upcoming' };

/**
 * How far from a scheduled time a run still counts as *that* firing. Derived from
 * the trigger's own cadence: half the tightest gap between its occurrences, so a
 * five-minute cron can't have one run satisfy two slots, capped at 30 minutes so
 * a daily job's match can't drift half a day.
 *
 * Half the gap is a ceiling, not a suggestion. This used to floor the result at
 * 60s *after* halving, which for an every-minute cron produced a tolerance as
 * wide as the entire period — one run then satisfied both of its neighbouring
 * slots and the view reported twice the runs that happened. Since cron cannot
 * fire more often than once a minute, half the gap is never so small that a
 * floor is needed to keep it usable.
 */
export function matchToleranceMs(sortedTimes: readonly number[]): number {
  let minGap = Number.POSITIVE_INFINITY;
  for (let i = 1; i < sortedTimes.length; i += 1) {
    minGap = Math.min(minGap, sortedTimes[i]! - sortedTimes[i - 1]!);
  }
  if (!Number.isFinite(minGap)) {
    return MAX_MATCH_TOLERANCE_MS;
  }
  return Math.min(minGap / 2, MAX_MATCH_TOLERANCE_MS);
}

/**
 * Resolves one scheduled instant against the run history for its trigger.
 *
 * `loaded` and `coverageStartMs` are both load-bearing: absence of a run only
 * means "didn't run" once history is in *and* the window was actually fetched
 * back this far. The batch query drops its oldest rows when it hits the row cap,
 * so judging a slot below the coverage boundary would report misses for runs that
 * happened and simply weren't returned.
 */
export function resolveOutcome(
  targetMs: number,
  nowMs: number,
  records: readonly ScheduledRunRecord[],
  toleranceMs: number,
  loaded: boolean,
  coverageStartMs: number = Number.NEGATIVE_INFINITY
): OccurrenceOutcome {
  if (targetMs > nowMs) {
    return { kind: 'upcoming' };
  }
  if (!loaded) {
    return { kind: 'pending' };
  }
  let best: ScheduledRunRecord | null = null;
  let bestDelta = Number.POSITIVE_INFINITY;
  for (const record of records) {
    const delta = Math.abs(record.at.getTime() - targetMs);
    // Strictly inside the window. Each slot is judged on its own, so at exactly
    // the tolerance — half the cadence — a run sitting midway between two slots
    // would be claimed by both of them.
    if (delta < toleranceMs && delta < bestDelta) {
      best = record;
      bestDelta = delta;
    }
  }
  if (best === null) {
    // Older than the history we were given: unknowable, not missed.
    if (targetMs < coverageStartMs) {
      return { kind: 'pending' };
    }
    // A slot that only just came due hasn't been missed — the runner has until
    // the end of its grace window to show up.
    return nowMs - targetMs < toleranceMs
      ? { kind: 'pending' }
      : { kind: 'no-record' };
  }
  return { kind: 'ran', status: best.status };
}

/**
 * What a day's worth of one trigger's occurrences adds up to, for a rolled-up
 * row. Deliberately not a tone or a label — those are the view's business; this
 * only decides which claim the counts support.
 *
 * `unknown` is the one that matters: an occurrence can also be *pending* (run
 * history not loaded, or older than the coverage boundary), and pending slots
 * land in none of these counters. Falling through to a `ran` claim reported
 * "0 ran" in green for a day the view had explicitly refused to judge.
 */
export type RollupVerdict =
  | { kind: 'failed'; failed: number; count: number }
  | { kind: 'ran'; ran: number }
  | { kind: 'unrecorded' }
  | { kind: 'unknown' };

export function rollupVerdict(counts: {
  ran: number;
  failed: number;
  unrecorded: number;
  count: number;
}): RollupVerdict {
  if (counts.failed > 0) {
    return { kind: 'failed', failed: counts.failed, count: counts.count };
  }
  if (counts.ran > 0) {
    return { kind: 'ran', ran: counts.ran };
  }
  if (counts.unrecorded > 0) {
    return { kind: 'unrecorded' };
  }
  // Everything left is upcoming or pending: nothing to claim either way.
  return { kind: 'unknown' };
}
